import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

export interface Check {
  id: number;
  amount: number;
  check_number: string;
  bank: string | null;
  reception_date: string | null;
  collection_date: string | null;
  check_status: string;
  reference: string | null;
  created_at: string;
  client_name: string;
  sales_request_id: string | null;
  client_id: string;
  factura_number: string | null;
  sales_request_data: any;
}

const fetchChecks = async (): Promise<Check[]> => {
  const { data, error } = await supabase
    .from("payments")
    .select(
      `
      id,
      amount,
      check_number,
      bank,
      reception_date,
      collection_date,
      check_status,
      reference,
      created_at,
      client,
      sales_request,
      clients(name),
      sales_request(id)
    `
    )
    .eq("payment_method", "check")
    .order("created_at", { ascending: false }); // Most recent first

  if (error) throw error;

  // Get all unique sales request IDs to fetch their documents
  const salesRequestIds =
    data
      ?.map((check: any) => check.sales_request?.id || check.sales_request)
      .filter(Boolean) || [];

  // Fetch factura documents for all sales requests (in batches to avoid URL length limits)
  let salesRequestDocuments: Record<string, string> = {};
  if (salesRequestIds.length > 0) {
    // Split into batches of 100 to avoid URL length limits
    const batchSize = 100;
    const batches = [];

    for (let i = 0; i < salesRequestIds.length; i += batchSize) {
      batches.push(salesRequestIds.slice(i, i + batchSize));
    }

    // Process each batch
    for (const batch of batches) {
      try {
        const { data: documentsData, error: docsError } = await supabase
          .from("sales_request_documents")
          .select("sales_request_id, bsale_number")
          .eq("document_type", "factura")
          .in("sales_request_id", batch);

        if (docsError) {
          console.error(
            "Error fetching sales request documents batch:",
            docsError
          );
        } else {
          // Create a map of sales_request_id -> factura_number
          documentsData?.forEach((doc: any) => {
            salesRequestDocuments[doc.sales_request_id] = doc.bsale_number;
          });
        }
      } catch (error) {
        console.error("Error processing batch:", error);
      }
    }
  }

  const formattedChecks: Check[] =
    data?.map((check: any) => {
      const salesRequestId =
        check.sales_request?.id || check.sales_request || null;
      const facturaNumber = salesRequestId
        ? salesRequestDocuments[salesRequestId]
        : null;

      return {
        id: check.id,
        amount: check.amount,
        check_number: check.check_number,
        bank: check.bank,
        reception_date: check.reception_date,
        collection_date: check.collection_date,
        check_status: check.check_status || "pending",
        reference: check.reference,
        created_at: check.created_at,
        client_name: check.clients?.name || "Cliente no encontrado",
        sales_request_id: salesRequestId,
        client_id: check.client || "",
        factura_number: facturaNumber,
        sales_request_data: check.sales_request,
      };
    }) || [];

  return formattedChecks;
};

export const useChecks = () => {
  return useQuery({
    queryKey: ["checks"],
    queryFn: fetchChecks,
    staleTime: 1000 * 60 * 2, // Data is fresh for 2 minutes
    gcTime: 1000 * 60 * 10, // Cache for 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
